import { ChangeDetectionStrategy, Component, Input, OnChanges, OnInit } from '@angular/core';
import { CalculatorResult } from '../../../../models/analysis/solar-calculator-result';
import { ResultListValueField } from '../../model/result-list-value-field';
import * as dayjs from 'dayjs';
import { LanguageService } from '../../../../../services/language.service';
import { UnsubscribeOnDestroyAdapter } from '../../../../adapters/unsubscibe-on-destroy.adapter';

@Component({
  selector: 'psc-monthly-values',
  templateUrl: './monthly-values.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MonthlyValuesComponent extends UnsubscribeOnDestroyAdapter implements OnChanges, OnInit {
  get selectedMonth(): number {
    return this._selectedMonth;
  }

  @Input()
  set selectedMonth(value: number) {
    this._selectedMonth = value;
    this.setValues();
  }

  monthsLocalized: string[];
  _selectedMonth: number;
  currentYear: string;
  @Input() solarCalculatorResult: CalculatorResult;
  @Input() showTitle: boolean = true;
  @Input() showDebt: boolean = false;


  monthlyValues: ResultListValueField[] = [];

  constructor(
    private languageService: LanguageService,
  ) {
    super();
  }

  ngOnInit() {
    this.setMonths();
    this.subs.sink = this.languageService.languageChanged$.subscribe(() => {
      this.setMonths();
    });
  }

  setMonths() {
    this.monthsLocalized = dayjs.months();
  }


  ngOnChanges() {
    this.setValues();
  }

  private setValues() {
    if (this._selectedMonth === undefined ||
      this._selectedMonth === null ||
      this._selectedMonth < 0 ||
      this._selectedMonth > 11 ||
      !this.solarCalculatorResult) {
      return;
    }

    const currency = this.solarCalculatorResult.currency;
    const monthlyResult = this.solarCalculatorResult.monthlyResult[this._selectedMonth];
    this.currentYear = monthlyResult.year;

    this.monthlyValues = [
      {
        labelKey: 'total-consumption-kwh',
        value: monthlyResult.consumptionKWh,
        unit: 'kWh',
      },
      {
        labelKey: 'total-consumption-high-kwh',
        value: monthlyResult.consumptionHighKWh,
        unit: 'kWh',
      },
      {
        labelKey: 'total-consumption-low-kwh',
        value: monthlyResult.consumptionLowKWh,
        unit: 'kWh',
      },
      {
        labelKey: 'total-production-kwh',
        value: monthlyResult.productionKWh,
        unit: 'kWh',
      },
      {
        labelKey: 'energy-from-grid-kwh',
        value: monthlyResult.energyFromGridKWh,
        unit: 'kWh',
      },
      {
        labelKey: 'energy-from-grid-price-per-kwh',
        value: monthlyResult.energyFromGridPricePerkWh,
        unit: currency,
      },
      {
        labelKey: 'feed-in-energy-to-grid-kwh',
        value: monthlyResult.feedInEnergyToGridKWh,
        unit: 'kWh',
      },
      {
        labelKey: 'feed-in-energy-to-grid-price-per-kwh',
        value: monthlyResult.feedInEnergyToGridPricePerkWh,
        unit: currency,
      },
      {
        labelKey: 'saving-energy-from-power-plant-kwh',
        value: monthlyResult.savingEnergyFromPowerPlantKWh,
        unit: 'kWh',
      },
      {
        labelKey: 'peak-power-price-per-kw',
        value: monthlyResult.peakPowerPricePerkW,
        unit: currency,
      },
      {
        labelKey: 'consumption-month-bill-money',
        value: monthlyResult.consumptionMoney,
        unit: currency,
      },
      {
        labelKey: 'power-peak-money',
        value: monthlyResult.powerPeakMoney,
        unit: currency,
      },
      {
        labelKey: 'power-peak-money-after-solar',
        value: monthlyResult.powerPeakMoneyAfterSolar,
        unit: currency,
      },
      {
        labelKey: 'actual-bill-money',
        value: monthlyResult.actualBillMoney,
        unit: currency,
      },
      {
        labelKey: 'saving-energy-from-power-plant',
        value: monthlyResult.savingEnergyFromPowerPlantMoney,
        unit: currency,
      },
      {
        labelKey: 'feed-in-to-grid',
        value: monthlyResult.feedInEnergyToGridMoney,
        unit: currency,
      },
      {
        labelKey: 'peak-power-saving-money',
        value: monthlyResult.powerPeakSavingMoney,
        unit: currency,
      },
      {
        labelKey: 'monthly-saving-money',
        value: monthlyResult.savingMoney,
        unit: currency,
      },
    ];
  }
}
